// src/pages/projects/AllExpensesPage.jsx
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Card, CardContent } from '@/components/ui/card';
import { Loader, Download, ArrowLeft, Eye, FileText, X } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { formatCurrency } from '@/lib/app-utils';
import { accountingAPI } from '@/services/accounting';
import { overheadAPI } from '@/services/overhead';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';

const AllExpensesPage = () => {
  const navigate = useNavigate();
  const [projectExpenses, setProjectExpenses] = useState([]);
  const [overheadExpenses, setOverheadExpenses] = useState([]);
  const [loading, setLoading] = useState(true);

  const [viewingFiles, setViewingFiles] = useState(false);
  const [currentFiles, setCurrentFiles] = useState([]);
  const [currentTitle, setCurrentTitle] = useState('');
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  const parseAttachment = (attachment) => {
    if (!attachment) return [];
    try {
      const parsed =
        typeof attachment === 'string' ? JSON.parse(attachment) : attachment;
      if (Array.isArray(parsed)) return parsed;
      return [parsed];
    } catch {
      return [];
    }
  };

  const getFileUrlFromAttachment = (file) => {
    if (!file) return null;
    if (file.fileUrl) return file.fileUrl;
    if (file.data) return file.data;
    if (file.url) return file.url;
    if (file.stored_filename) {
      return `${import.meta.env.VITE_API_BASE_URL}/overhead/download-file.php?id=${file.id}`;
    }
    return null;
  };

  const fetchOverheadFiles = async (expenseId) => {
    try {
      const response = await overheadAPI.getFiles(expenseId);
      if (response.success && response.data) {
        return response.data.map((file) => ({
          id: file.id,
          fileName: file.original_filename,
          fileSize: file.filesize
            ? `${(file.filesize / 1024 / 1024).toFixed(2)} MB`
            : null,
          fileUrl: `${import.meta.env.VITE_API_BASE_URL}/overhead/download-file.php?id=${file.id}`,
          previewUrl: `${import.meta.env.VITE_API_BASE_URL}/overhead/download-file.php?id=${file.id}&preview=1`,
          mimeType: file.filetype || file.mime_type,
          uploadedAt: file.uploaded_at,
          originalFilename: file.original_filename,
          storedFilename: file.stored_filename
        }));
      }
      return [];
    } catch (error) {
      console.error('Failed to fetch overhead files:', error);
      return [];
    }
  };

  const openFilesViewer = (expense) => {
    const { type, id, attachment } = expense;

    const loadFiles = async () => {
      try {
        setLoadingFiles(true);
        let files = [];

        if (type === 'Overhead' && id) {
          files = await fetchOverheadFiles(id);
        } else {
          files = parseAttachment(attachment);
          files = files.map((f) => ({
            ...f,
            fileName: f.fileName || f.name || f.original_filename,
            fileSize: f.fileSize || f.size,
            fileUrl: getFileUrlFromAttachment(f),
            previewUrl:
              f.previewUrl ||
              (f.id
                ? `${import.meta.env.VITE_API_BASE_URL}/overhead/download-file.php?id=${f.id}&preview=1`
                : null)
          }));
        }

        if (!files.length) {
          toast({
            title: 'No Files',
            description: 'No attachments found for this expense.',
            variant: 'destructive'
          });
          return;
        }

        setCurrentFiles(files);
        setCurrentTitle(expense.description || expense.title || 'Attached Files');
        setPreviewFile(null);
        setViewingFiles(true);
      } catch (error) {
        console.error('Error opening files viewer:', error);
        toast({
          title: 'Error',
          description: 'Failed to load attached files.',
          variant: 'destructive'
        });
      } finally {
        setLoadingFiles(false);
      }
    };

    loadFiles();
  };

  const openFileWithAuth = async (file) => {
    const url = file.previewUrl || file.fileUrl;
    if (!url) {
      toast({
        title: 'File Error',
        description: 'Could not locate file.',
        variant: 'destructive'
      });
      return;
    }

    try {
      const response = await fetch(
        file.previewUrl || `${url}${url.includes('?') ? '&' : '?'}preview=1`,
        { credentials: 'include' }
      );

      if (!response.ok) throw new Error('Failed to fetch file');

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const isPdf =
        file.mimeType === 'application/pdf' ||
        file.fileName?.toLowerCase().endsWith('.pdf');

      const isImage =
        file.mimeType?.startsWith('image/') ||
        /\.(jpg|jpeg|png|gif|webp)$/i.test(file.fileName || '');

      if (isPdf || isImage) {
        const win = window.open('', '_blank', 'noopener,noreferrer');
        if (win) {
          if (isPdf) {
            win.document.write(`
              <!DOCTYPE html>
              <html><head><title>${file.fileName}</title>
              <style>body{margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#f1f5f9}</style>
              </head><body>
              <iframe src="${blobUrl}" style="width:100%;height:100vh;border:none;"></iframe>
              </body></html>
            `);
            win.document.close();
          } else {
            win.document.write(`
              <!DOCTYPE html>
              <html><head><title>${file.fileName}</title>
              <style>body{margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#f1f5f9}</style>
              </head><body>
              <img src="${blobUrl}" style="max-width:100%;max-height:100vh;object-fit:contain;" />
              </body></html>
            `);
            win.document.close();
          }
          win.onbeforeunload = () => URL.revokeObjectURL(blobUrl);
        }
      } else {
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = file.fileName || 'download';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      }
    } catch (error) {
      console.error('Error opening file:', error);
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleOpenSingleFile = (file) => {
    openFileWithAuth(file);
  };

  const loadExpenses = async () => {
    try {
      setLoading(true);

      const accRes = await accountingAPI.getAllAccounting({
  type: 'expense',
  status: 'Approved',
  include_archived: 1
});

      if (!accRes.success) {
        throw new Error(accRes.error || 'Failed to load project expenses');
      }

      setProjectExpenses(accRes.data?.expenses || []);
      setOverheadExpenses(accRes.data?.overhead || []);
    } catch (error) {
      console.error(error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load expenses data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadExpenses();
  }, []);

  const allExpenses = [
    ...projectExpenses.map((e) => ({
      type: 'Project',
      id: e.id,
      projectId: e.projectId || e.project_id,
      projectName: e.projectName || e.project_name || '-',
      companyName: e.companyName || e.company_name || '-',
      description: e.description,
      category: e.category,
      amount: e.amount,
      date: e.date,
      status: e.status,
      projectStatus: e.projectStatus || e.project_status || '-',
      isArchivedProject: !!e.isArchivedProject,
      attachment: e.attachment || null
    })),
    ...overheadExpenses.map((e) => ({
      type: 'Overhead',
      id: e.id,
      projectId: null,
      projectName: '-',
      companyName: '-',
      description: e.description || e.title,
      category: e.category,
      amount: e.amount,
      date: e.date,
      status: e.status || 'Approved',
      projectStatus: e.projectStatus || 'Overhead',
      isArchivedProject: false,
      attachment: e.attachment || null
    }))
  ];

  const totalProjectExpenses = projectExpenses.reduce(
    (sum, e) => sum + (parseFloat(e.amount) || 0),
    0
  );

  const totalOverhead = overheadExpenses.reduce(
    (sum, e) => sum + (parseFloat(e.amount) || 0),
    0
  );

  const totalExpenses = totalProjectExpenses + totalOverhead;

  const handleExportExcel = async () => {
    if (allExpenses.length === 0) {
      toast({
        title: 'No Data',
        description: 'No expenses to export.',
        variant: 'destructive'
      });
      return;
    }

    const XLSX = await import('xlsx');

    const rows = allExpenses.map((item) => ({
      Type: item.type,
      Project: item.projectName,
      Company: item.companyName,
      'Project Status': item.projectStatus,
      Archived: item.isArchivedProject ? 'Yes' : 'No',
      Category: item.category,
      Description: item.description,
      'Amount (AED)': parseFloat(item.amount || 0).toFixed(2),
      Date: item.date ? new Date(item.date).toLocaleDateString() : '-',
      Status: item.status
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Expenses');
    XLSX.writeFile(
      wb,
      `All_Expenses_${new Date().toISOString().split('T')[0]}.xlsx`
    );
  };

  const FilePreviewPanel = ({ file, onClose }) => {
    const [blobUrl, setBlobUrl] = useState(null);
    const [fetchError, setFetchError] = useState(false);
    const [fetching, setFetching] = useState(false);

    useEffect(() => {
      if (!file?.previewUrl) return;
      let objectUrl = null;
      setFetchError(false);
      setBlobUrl(null);

      const load = async () => {
        try {
          setFetching(true);
          const res = await fetch(file.previewUrl, { credentials: 'include' });
          if (!res.ok) throw new Error('fetch failed');
          const blob = await res.blob();
          objectUrl = URL.createObjectURL(blob);
          setBlobUrl(objectUrl);
        } catch {
          setFetchError(true);
        } finally {
          setFetching(false);
        }
      };

      load();
      return () => {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      };
    }, [file?.previewUrl]);

    if (!file) return null;

    const isPdf =
      file.mimeType === 'application/pdf' ||
      file.fileName?.toLowerCase().endsWith('.pdf');

    const isImage =
      file.mimeType?.startsWith('image/') ||
      /\.(jpg|jpeg|png|gif|webp)$/i.test(file.fileName || '');

    return (
      <div className="mt-4 border rounded-lg overflow-hidden bg-slate-100">
        <div className="flex items-center justify-between px-3 py-2 bg-slate-200 text-xs font-medium text-stone-600">
          <span className="truncate">{file.fileName || file.originalFilename}</span>
          <button
            onClick={onClose}
            className="text-stone-400 hover:text-red-500 ml-2"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {fetching ? (
          <div className="flex justify-center items-center h-[200px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
          </div>
        ) : fetchError ? (
          <div className="flex flex-col items-center justify-center py-10 text-stone-500 gap-3">
            <FileText className="w-10 h-10 text-stone-300" />
            <p className="text-sm">Could not load preview.</p>
            <Button
              size="sm"
              onClick={() => handleOpenSingleFile(file)}
              className="bg-blue-600 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Download File
            </Button>
          </div>
        ) : blobUrl && isPdf ? (
          <iframe
            src={blobUrl}
            className="w-full h-[450px] border-0"
            title={file.fileName}
          />
        ) : blobUrl && isImage ? (
          <img
            src={blobUrl}
            alt={file.fileName}
            className="w-full max-h-[400px] object-contain p-4"
          />
        ) : blobUrl ? (
          <div className="flex flex-col items-center justify-center py-10 text-stone-500 gap-3">
            <FileText className="w-10 h-10 text-stone-300" />
            <p className="text-sm">Preview not available for this file type.</p>
            <Button
              size="sm"
              onClick={() => handleOpenSingleFile(file)}
              className="bg-blue-600 text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Download File
            </Button>
          </div>
        ) : null}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>All Expenses | Stone Beam</title>
      </Helmet>

      <div className="p-6 space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">All Expenses</h1>
            <p className="text-sm text-muted-foreground">
              Approved project expenses and overhead costs
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/projects')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Projects
            </Button>
            <Button onClick={handleExportExcel}>
              <Download className="h-4 w-4 mr-2" />
              Export Excel
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-xs uppercase text-muted-foreground font-semibold">
                Project Expenses
              </p>
              <p className="text-2xl font-bold text-red-700">
                {formatCurrency(totalProjectExpenses)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Entries: {projectExpenses.length}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <p className="text-xs uppercase text-muted-foreground font-semibold">
                Overhead Expenses
              </p>
              <p className="text-2xl font-bold text-orange-700">
                {formatCurrency(totalOverhead)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Entries: {overheadExpenses.length}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <p className="text-xs uppercase text-muted-foreground font-semibold">
                Archived Project Expenses
              </p>
              <p className="text-2xl font-bold text-blue-700">
                {
                  allExpenses.filter(
                    (item) => item.type === 'Project' && item.isArchivedProject
                  ).length
                }
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Entries linked to archived projects
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <p className="text-xs uppercase text-muted-foreground font-semibold">
                Total Expenses
              </p>
              <p className="text-2xl font-bold text-red-800">
                {formatCurrency(totalExpenses)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Project + Overhead (Approved)
              </p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Project Status</TableHead>
                  <TableHead>Archived</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-center">Files</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allExpenses.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={11}
                      className="text-center py-8 text-muted-foreground"
                    >
                      No expenses found.
                    </TableCell>
                  </TableRow>
                ) : (
                  allExpenses.map((item, idx) => (
                    <TableRow key={`${item.type}-${item.id}-${idx}`}>
                      <TableCell>{item.type}</TableCell>
                      <TableCell>
                        {item.projectId ? (
                          <button
                            className="text-blue-600 hover:underline"
                            onClick={() =>
                              navigate(`/projects/${item.projectId}`)
                            }
                          >
                            {item.projectName}
                          </button>
                        ) : (
                          item.projectName
                        )}
                      </TableCell>
                      <TableCell>{item.companyName}</TableCell>
                      <TableCell>{item.projectStatus}</TableCell>
                      <TableCell>
                        {item.isArchivedProject ? 'Yes' : 'No'}
                      </TableCell>
                      <TableCell>{item.category}</TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell className="text-center">
                        {(item.attachment || (item.type === 'Overhead' && item.id)) ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => openFilesViewer(item)}
                            title="View attached files"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="font-semibold">
                        {formatCurrency(item.amount)}
                      </TableCell>
                      <TableCell>
                        {item.date
                          ? new Date(item.date).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell>{item.status}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={viewingFiles}
        onOpenChange={(open) => {
          setViewingFiles(open);
          if (!open) {
            setCurrentFiles([]);
            setPreviewFile(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Attached Files</DialogTitle>
            {currentTitle && (
              <DialogDescription>{currentTitle}</DialogDescription>
            )}
          </DialogHeader>

          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {loadingFiles ? (
              <div className="flex justify-center py-8">
                <Loader className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : currentFiles.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground text-sm">
                No files attached.
              </div>
            ) : (
              currentFiles.map((file, idx) => {
                const isActive = previewFile?.id === file.id;
                return (
                  <div
                    key={file.id || idx}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                      isActive
                        ? 'bg-blue-50 border-blue-300'
                        : 'bg-slate-50 hover:bg-slate-100'
                    }`}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <FileText
                        className={`h-5 w-5 flex-shrink-0 ${
                          isActive ? 'text-blue-700' : 'text-blue-600'
                        }`}
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">
                          {file.fileName ||
                            file.name ||
                            file.originalFilename ||
                            `File ${idx + 1}`}
                        </p>
                        {(file.fileSize || file.filesize) && (
                          <p className="text-xs text-muted-foreground">
                            {file.fileSize ||
                              `${(file.filesize / 1024 / 1024).toFixed(2)} MB`}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setPreviewFile(isActive ? null : file)}
                        className={
                          isActive
                            ? 'text-blue-700'
                            : 'text-blue-500 hover:text-blue-700'
                        }
                        title="Preview"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenSingleFile(file)}
                        title="Download/Open"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {previewFile && (
            <FilePreviewPanel
              file={previewFile}
              onClose={() => setPreviewFile(null)}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AllExpensesPage;